const NICE_FRACTIONS = [1, 2, 2.5, 5, 10];
const TARGET_INTERVALS = 5;
// Guards tick generation against floating point drift (e.g. 0.1 + 0.2)
const EPSILON = 1e-9;

/** Rounds `raw` up to the nearest 1/2/2.5/5 × 10^n step */
export const getNiceStep = (raw: number): number => {
  if (!Number.isFinite(raw) || raw <= 0) {
    return 1;
  }

  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const fraction = raw / magnitude;
  const niceFraction = NICE_FRACTIONS.find(step => step >= fraction - EPSILON) ?? 10;

  return niceFraction * magnitude;
};

const buildTicks = (step: number, max: number): number[] => {
  const ticks: number[] = [];
  for (let index = 0; index * step <= max + EPSILON; index++) {
    ticks.push(Number((index * step).toPrecision(12)));
  }
  return ticks;
};

export type BarChartScales = {
  value: { domain: [number, number]; ticks: number[] };
  share: { domain: [number, number]; ticks: number[] };
};

/**
 * Value (left) and share (right) axes describe the same bars, so the share axis is the value domain
 * expressed as a percentage of `total` — only its labelled ticks are rounded independently.
 */
export const getBarChartScales = (maxValue: number, total: number): BarChartScales => {
  const valueStep = getNiceStep(maxValue / TARGET_INTERVALS);
  const valueMax = Math.max(Math.ceil(maxValue / valueStep - EPSILON), 1) * valueStep;

  const shareMax = total > 0 ? (valueMax / total) * 100 : 100;
  const shareStep = getNiceStep(shareMax / TARGET_INTERVALS);

  return {
    value: { domain: [0, valueMax], ticks: buildTicks(valueStep, valueMax) },
    share: { domain: [0, shareMax], ticks: buildTicks(shareStep, shareMax) },
  };
};
